import React, { useLayoutEffect, useRef, useState } from 'react';
import cx from 'classnames';
import moment from 'moment';

import {
  ArrowBackward,
  ArrowForward,
  BackSm,
  NextSm,
  SortDown,
} from '../../Icons';

import { DAY_NAMES, MONTH_NAMES } from '../DateRangePicker/DateList';

import type { DateTimeValueT } from './types';

interface IDatePickerProps {
  year: number;
  month: number;
  date: number | undefined;
  onSelect: (year: number, month: number, date: number) => void;
}

const DatePicker = React.memo(
  ({ year, month, date, onSelect }: IDatePickerProps) => {
    const [temp_Y_M, setTemp_Y_M] = useState({ year, month });

    const [pickYear, setPickYear] = useState<boolean>(false);
    const [headerLabel, setHeaderLabel] = useState<string>(
      date
        ? `${DAY_NAMES[new Date(year, month, date).getDay()]}, ${MONTH_NAMES[
            month
          ].slice(0, 3)} ${date}`
        : ' _ _ '
    );

    const handleNextMonth = () => {
      setTemp_Y_M((value) => ({
        year: value.year + (value.month == 11 ? 1 : 0),
        month: (value.month + 1) % 12,
      }));
    };
    const handlePrevMonth = () => {
      setTemp_Y_M((value) => ({
        year: value.year - (value.month == 0 ? 1 : 0),
        month: (value.month + 11) % 12,
      }));
    };

    return (
      <>
        <div className="block md:hidden text-[24px] leading-[30px] py-5 px-[27px] border-b border-primary">
          {headerLabel}
        </div>
        <div className="date-time-select">
          <YearAndMonthToggler
            handleNextMonth={handleNextMonth}
            handlePrevMonth={handlePrevMonth}
            currentMonth={temp_Y_M.month}
            currentYear={temp_Y_M.year}
            togglePickYear={() => setPickYear(!pickYear)}
            pickYear={pickYear}
          />
          <div className="relative">
            <div className={cx('bg-white', { invisible: pickYear })}>
              <DateList
                handleSelectDate={(date: number, day: number) => {
                  setHeaderLabel(
                    `${DAY_NAMES[day]}, ${MONTH_NAMES[temp_Y_M.month].slice(
                      0,
                      3
                    )} ${date}`
                  );
                  onSelect(temp_Y_M.year, temp_Y_M.month, date);
                }}
                currentYear={temp_Y_M.year}
                currentMonth={temp_Y_M.month}
                currentDate={{ year, month, date }}
              />
            </div>
            {pickYear && (
              <YearList
                onSelectYear={(y) => {
                  setTemp_Y_M((value) => ({
                    ...value,
                    year: y,
                  }));
                  setPickYear(false);
                }}
                currentYear={temp_Y_M.year}
              />
            )}
          </div>
        </div>
      </>
    );
  }
);

interface YearAndMonthTogglerProps {
  handleNextMonth: () => void;
  handlePrevMonth: () => void;
  currentMonth: number;
  currentYear: number;
  togglePickYear: () => void;
  pickYear: boolean;
}

const YearAndMonthToggler = ({
  handleNextMonth,
  handlePrevMonth,
  currentMonth,
  currentYear,
  togglePickYear,
  pickYear,
}: YearAndMonthTogglerProps) => {
  return (
    <div className="flex items-center relative justify-between md:justify-center mb-2.5 md:mb-5">
      <div
        className="flex flex-row items-center justify-center cursor-pointer z-[1]"
        onClick={togglePickYear}
      >
        <p className="text-base font-medium md:font-bold leading-10 md:leading-6 px-2.5">
          {MONTH_NAMES[currentMonth]} {currentYear}
        </p>
        <SortDown
          width={18}
          height={18}
          className={cx('md:hidden transition-transform', {
            'rotate-180': pickYear,
          })}
        />
      </div>
      {!pickYear && (
        <>
          <div className="hidden md:flex w-full absolute justify-between px-3">
            <BackSm
              className="cursor-pointer fill-uta-grey"
              onClick={handlePrevMonth}
            />
            <NextSm
              className="cursor-pointer fill-uta-grey"
              onClick={handleNextMonth}
            />
          </div>
          <div className="flex md:hidden gap-[5px] h-10">
            <ArrowBackward
              width={22}
              height={22}
              className="cursor-pointer m-[9px]"
              onClick={handlePrevMonth}
            />
            <ArrowForward
              width={22}
              height={22}
              className="cursor-pointer m-[9px]"
              onClick={handleNextMonth}
            />
          </div>
        </>
      )}
    </div>
  );
};

interface YearListProps {
  onSelectYear: (year: number) => void;
  currentYear: number;
}

const YearList = ({ onSelectYear, currentYear }: YearListProps) => {
  const listRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const timer = setTimeout(() => {
      if (listRef?.current) {
        listRef.current.scrollTop =
          Math.floor((currentYear - 1900) / 3) * 64 - 130;
      }
    }, 10);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <div
      className="date-list-container px-2 absolute top-0 h-[calc(100%_-_15px)]"
      ref={listRef}
    >
      <div className="flex flex-col gap-6 w-full">
        {Array.from({ length: 67 }).map((_, index) => (
          <div
            className="flex items-center justify-between gap-[26px]"
            key={index}
          >
            {[0, 1, 2].map((i) => {
              const year = 1900 + index * 3 + i;
              return (
                <p
                  onClick={() => onSelectYear(year)}
                  className={cx(
                    'text-center text-[16px] font-medium max-w-[72px] leading-10 cursor-pointer w-[72px]',
                    year === currentYear
                      ? 'text-white bg-uta-black'
                      : 'text-gray-900',
                    {
                      'border border-uta-black':
                        year === new Date().getFullYear(),
                    }
                  )}
                  key={year}
                >
                  {year}
                </p>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

interface DateListProps {
  handleSelectDate: (date: number, day: number) => void;
  currentDate: DateTimeValueT;
  currentMonth: number;
  currentYear: number;
}

const DateList = React.memo(
  ({
    handleSelectDate,
    currentDate,
    currentMonth,
    currentYear,
  }: DateListProps) => {
    const isToday = (day: number) =>
      moment(0, 'HH').diff(
        `${currentYear}-${currentMonth + 1}-${day}`,
        'days'
      ) === 0;
    const isSameMonth =
      currentDate.year === currentYear && currentDate.month === currentMonth;

    const start = new Date(currentYear, currentMonth).getDay();
    const length = new Date(currentYear, currentMonth + 1, 0).getDate();

    return (
      <>
        <div className="calendar-picker">
          {DAY_NAMES.map((day: string) => (
            <p className="week-name-cell" key={day}>
              {day.charAt(0)}
            </p>
          ))}
        </div>
        <div className="calendar-picker">
          {Array.from({ length }).map((_, index: number) => (
            <div
              className="calendar-picker-cell"
              key={index}
              style={index === 0 ? { gridColumn: start + 1 } : {}}
            >
              <p
                onClick={() => handleSelectDate(index + 1, (start + index) % 7)}
                className={cx(
                  'cursor-pointer',
                  isSameMonth && index + 1 === currentDate.date
                    ? 'text-white bg-uta-black'
                    : 'text-gray-800 hover:bg-[#0000000D]',
                  {
                    'border border-uta-black':
                      isToday(index + 1) ||
                      (isSameMonth && index + 1 === currentDate.date),
                  }
                )}
              >
                {index + 1}
              </p>
            </div>
          ))}
        </div>
      </>
    );
  }
);
DateList.displayName = 'DateList';

export default DatePicker;
